import { useState, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useGetPublishedAnnouncements } from 'src/services/announcements/get-published-announcements'
import { Announcement } from 'src/types/announcement'
import {
    getSeenAnnouncementIds,
    markAnnouncementAsSeen,
    isNotificationDismissed,
    setNotificationDismissed,
    clearNotificationDismissed
} from 'src/utils/announcement-storage'

const NOTIFICATION_FETCH_LIMIT = 10 // Fetch last 10 announcements
const NOTIFICATION_DISPLAY_LIMIT = 3 // Show max 3 in notification
const REFETCH_INTERVAL = 30 * 60 * 1000 // 30 minutes

export interface UseAnnouncementNotificationsReturn {
    unseenAnnouncements: Announcement[]
    isVisible: boolean
    isLoading: boolean
    handleAnnouncementClick: (announcement: Announcement) => void
    handleClose: () => void
}

export const useAnnouncementNotifications = (): UseAnnouncementNotificationsReturn => {
    const history = useHistory()
    const [isManuallyDismissed, setIsManuallyDismissed] = useState(false)
    const [seenAnnouncementsVersion, setSeenAnnouncementsVersion] = useState(0) // Track localStorage changes
    const [lastDismissedAnnouncementIds, setLastDismissedAnnouncementIds] = useState<string[]>([]) // Track what was dismissed

    // Fetch recent announcements with auto-refetch
    const { data, isLoading } = useGetPublishedAnnouncements({
        pageSize: NOTIFICATION_FETCH_LIMIT,
        page: 1
    }, {
        refetchInterval: REFETCH_INTERVAL,
        refetchIntervalInBackground: true,
        staleTime: 5 * 60 * 1000, // 5 minutes stale time
    })

    // Initialize manual dismiss state from localStorage
    useEffect(() => {
        setIsManuallyDismissed(isNotificationDismissed())
    }, [])

    // Listen for localStorage changes from external sources
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'vdex_seen_announcements') {
                // Force re-evaluation when seen announcements are updated from outside
                setSeenAnnouncementsVersion(prev => prev + 1)
            }
        }

        // Listen for storage events (works for changes from other tabs/windows)
        window.addEventListener('storage', handleStorageChange)

        // Listen for custom events (works for changes in the same tab)
        const handleCustomStorageUpdate = () => {
            setSeenAnnouncementsVersion(prev => prev + 1)
        }

        window.addEventListener('vdex-announcements-updated', handleCustomStorageUpdate)

        return () => {
            window.removeEventListener('storage', handleStorageChange)
            window.removeEventListener('vdex-announcements-updated', handleCustomStorageUpdate)
        }
    }, [])

    // Filter unseen announcements
    const unseenAnnouncements = useMemo(() => {
        if (!data?.announcements) return []

        const seenIds = getSeenAnnouncementIds()
        const unseen = data.announcements.filter(announcement =>
            !seenIds.includes(announcement.id)
        )

        // Return max 3 announcements for display
        return unseen.slice(0, NOTIFICATION_DISPLAY_LIMIT)
    }, [data?.announcements, seenAnnouncementsVersion]) // Add dependency on seen announcements version

    // Only reset manual dismiss when there are genuinely NEW announcements that weren't previously dismissed
    useEffect(() => {
        if (unseenAnnouncements.length > 0 && isManuallyDismissed) {
            const currentUnseenIds = unseenAnnouncements.map(a => a.id)
            const hasNewAnnouncementsSinceLastDismiss = currentUnseenIds.some(id =>
                !lastDismissedAnnouncementIds.includes(id)
            )

            if (hasNewAnnouncementsSinceLastDismiss) {
                setIsManuallyDismissed(false)
                clearNotificationDismissed()
                setLastDismissedAnnouncementIds([]) // Reset dismissed tracking
            }
        }
    }, [unseenAnnouncements, isManuallyDismissed, lastDismissedAnnouncementIds])

    // Determine if notification should be visible
    const isVisible = useMemo(() => {
        return unseenAnnouncements.length > 0 && !isManuallyDismissed
    }, [unseenAnnouncements.length, isManuallyDismissed])

    // Handle clicking on an announcement
    const handleAnnouncementClick = (announcement: Announcement) => {
        markAnnouncementAsSeen(announcement.id)

        // Force re-evaluation of unseen announcements
        setSeenAnnouncementsVersion(prev => prev + 1)

        // Navigate to announcement detail page using React Router and pass announcement data
        history.push(`/announcements/${announcement.id}`, { announcement })
    }

    // Handle closing the notification card
    const handleClose = () => {
        // Remember which announcements were dismissed
        setLastDismissedAnnouncementIds(unseenAnnouncements.map(a => a.id))
        setIsManuallyDismissed(true)
        setNotificationDismissed(true)

        // Mark all announcements as read when the user closes the popup
        const seenIds = getSeenAnnouncementIds()
        const unseenRecords = data?.announcements.filter(announcement =>
            !seenIds.includes(announcement.id)
        ) ?? [];

        unseenRecords.forEach((item)=>{
            markAnnouncementAsSeen(item.id)
        })
    }

    return {
        unseenAnnouncements,
        isVisible,
        isLoading,
        handleAnnouncementClick,
        handleClose
    }
} 
